'use client'

import { useState, type ComponentProps } from 'react'
import Link from 'next/link'
import { Lock, ArrowRight, Loader2 } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { ToolEntryHero } from '@/components/tools/ToolEntryHero'
import { Button } from '@/components/ui/button'
import { openCheckout } from '@/lib/paddle/checkout'
import AeoResults from './components/AeoResults'
import { useToolRunOrCheckout } from '@/hooks/useToolRunOrCheckout'

const SCORE_CATEGORIES = [
  { label: 'Speakable Schema', color: '#8b5cf6', pts: '15 pts' },
  { label: 'Answer-First Structure', color: '#008dcc', pts: '20 pts' },
  { label: 'Structured Data Quality', color: '#10b981', pts: '25 pts' },
  { label: 'Data Extractability', color: '#FDC304', pts: '20 pts' },
  { label: 'Content Freshness', color: '#F97316', pts: '10 pts' },
  { label: 'Entity Authority', color: '#F90B8A', pts: '10 pts' },
]

// SEO copy, kept verbatim — rendered below the tool panel.
const SEO_PARAGRAPH =
  'AI search engines like ChatGPT, Perplexity, and Google AI Overviews do not just read your content. They extract specific data points, definitions, and structured elements. This tool scores how extractable your page is across 6 AEO categories.'

export default function AeoAuditorClient() {
  const [label, setLabel] = useState('')
  // The exact URL submitted, frozen so editing the input after the wall appears
  // cannot change what the $5 checkout pays to run.
  const [submittedUrl, setSubmittedUrl] = useState('')
  // Owner/paid → full result inline (free); everyone else → 402 → the $5 wall.
  const {
    run,
    loading,
    error: runError,
    result,
    walled,
  } = useToolRunOrCheckout<ComponentProps<typeof AeoResults>['result']>(
    '/api/tools/aeo-auditor'
  )

  // Start path: freeze the submitted URL, run (owner/paid inline, everyone
  // else hits the $5 wall), scroll to output. Input validation lives in
  // ToolEntryHero.
  function startAudit(trimmed: string) {
    setLabel(trimmed.replace(/^https?:\/\//, ''))
    setSubmittedUrl(trimmed)
    void run({ url: trimmed })
    setTimeout(() => {
      document
        .getElementById('aeo-output')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  return (
    <div className='animate-fade-in'>
      <div className='container mx-auto px-4 py-16 pb-24'>
        <LenisFadeIn>
          <div className='text-center mb-10'>
            <ToolEntryHero
              title='AEO Page Auditor: Score Any Page for AI Answer Engines'
              valueProp='Score how extractable your page is for AI answer engines across 6 AEO categories.'
              trustLine='$5 one-time · report opens here'
              inputLabel='Page URL to audit'
              placeholder='Enter a page URL (e.g. example.com/blog/my-post)'
              buttonLabel='Audit Page'
              exampleValue='pixelmojo.io/blogs'
              isLoading={loading}
              onSubmit={startAudit}
            />
            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mt-8'>
              {SEO_PARAGRAPH}
            </p>
          </div>
        </LenisFadeIn>

        {(loading || runError || result || walled) && (
          <div id='aeo-output' className='max-w-4xl mx-auto mb-12'>
            {loading && (
              <div className='flex items-center justify-center gap-3 rounded-2xl border border-line-strong bg-muted/50 p-8 text-sm text-muted-foreground'>
                <Loader2 size={18} className='animate-spin' />
                Auditing {label}…
              </div>
            )}

            {runError && !loading && (
              <p className='text-sm text-[#F90B8A] bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {runError}
              </p>
            )}

            {result && !loading && <AeoResults result={result} />}

            {walled && !loading && (
              <div className='max-w-2xl mx-auto'>
                <div className='rounded-2xl border border-[#7c3aed]/30 bg-[#7c3aed]/5 p-8 text-center'>
                  <div className='flex justify-center mb-3'>
                    <Lock size={22} className='text-[#7c3aed]' />
                  </div>
                  <p className='text-base font-semibold text-foreground mb-2'>
                    Run the AEO audit for {label}
                  </p>
                  <p className='text-sm text-muted-foreground mb-6 max-w-md mx-auto'>
                    Pay a one-time $5 to score{' '}
                    <span className='font-medium text-foreground'>{label}</span>{' '}
                    across all 6 AEO categories: every finding and the priority
                    fixes. Your report opens right here.
                  </p>
                  <div className='flex flex-col sm:flex-row gap-3 justify-center'>
                    <Button
                      type='button'
                      variant='default'
                      size='lg'
                      onClick={() =>
                        openCheckout('pack-single', 'tool_teaser', {
                          source: 'tool_run',
                          toolSlug: 'aeo-auditor',
                          url: submittedUrl,
                        })
                      }
                      className='h-12 px-7'
                    >
                      Run for $5
                      <ArrowRight size={16} />
                    </Button>
                    <Link
                      href='/platform#pricing'
                      className='inline-flex items-center justify-center gap-2 h-12 px-7 text-sm font-semibold text-foreground border border-border transition-colors hover:bg-muted'
                    >
                      See pricing
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <LenisFadeIn>
          <div className='max-w-3xl mx-auto mt-12'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
                What we check
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                {[
                  {
                    title: 'Speakable Schema',
                    desc: 'Does your Article JSON-LD include SpeakableSpecification? Do the CSS selectors target real page elements?',
                    color: '#8b5cf6',
                  },
                  {
                    title: 'Answer-First Structure',
                    desc: 'Single H1, valid heading hierarchy, question-format H2s, and concise definition paragraphs under each heading.',
                    color: '#008dcc',
                  },
                  {
                    title: 'Structured Data Quality',
                    desc: 'Article completeness (author, dateModified, publisher), Organization sameAs, FAQPage, BreadcrumbList, and entity-linked knowsAbout.',
                    color: '#10b981',
                  },
                  {
                    title: 'Data Extractability',
                    desc: 'HTML tables, ordered lists, StatBlock data callouts, and external source citations that AI engines can directly extract.',
                    color: '#FDC304',
                  },
                  {
                    title: 'Content Freshness',
                    desc: 'dateModified in schema, Last-Modified HTTP headers, and recency signals that affect AI re-crawl frequency.',
                    color: '#F97316',
                  },
                  {
                    title: 'Entity Authority',
                    desc: 'Organization sameAs links, Person credentials, DefinedTerm references, and OpenGraph/Twitter Card meta tags.',
                    color: '#F90B8A',
                  },
                ].map(item => (
                  <FeatureCard key={item.title} {...item} variant='row' />
                ))}
              </div>
            </div>
          </div>
        </LenisFadeIn>

        <LenisFadeIn>
          <div className='max-w-4xl mx-auto mt-16'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-2 text-center'>
                How We Score (100 Points Total)
              </h2>
              <p className='text-sm text-muted-foreground text-center mb-8 max-w-2xl mx-auto'>
                Your AEO score measures how extractable your page is for AI
                search engines. Each category targets a specific aspect of
                answer engine citation readiness. For what a good score looks
                like against real audit data, read{' '}
                <Link
                  href='/blogs/aeo-score-explained-checkers-grades-improvement'
                  className='text-foreground underline underline-offset-4 hover:text-primary transition-colors motion-reduce:transition-none'
                >
                  AEO Score Explained
                </Link>
                .
              </p>

              <div className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {SCORE_CATEGORIES.map(item => (
                    <div
                      key={item.label}
                      className='bg-background border border-line-strong rounded-xl p-4'
                    >
                      <div className='flex items-center justify-between mb-2'>
                        <div className='flex items-center gap-2'>
                          <span
                            className='h-2 w-2 rounded-full'
                            style={{ backgroundColor: item.color }}
                          />
                          <span className='text-sm font-semibold text-foreground'>
                            {item.label}
                          </span>
                        </div>
                        <span
                          className='text-xs font-semibold'
                          style={{ color: item.color }}
                        >
                          {item.pts}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='border-t border-line-strong pt-6'>
                  <h3 className='text-sm font-semibold text-foreground mb-3'>
                    Grade scale
                  </h3>
                  <div className='flex flex-wrap gap-3'>
                    {[
                      { grade: 'A', range: '85-100', color: '#22c55e' },
                      { grade: 'B', range: '70-84', color: '#008dcc' },
                      { grade: 'C', range: '55-69', color: '#FDC304' },
                      { grade: 'D', range: '40-54', color: '#f97316' },
                      { grade: 'F', range: '0-39', color: '#F90B8A' },
                    ].map(g => (
                      <span
                        key={g.grade}
                        className='text-xs font-medium'
                        style={{ color: g.color }}
                      >
                        {g.grade}: {g.range}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
